import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ClipboardCheck, Ban } from "lucide-react";
import api, { apiErr, fmtDate, money } from "@/lib/api";
import { Loading, Field } from "@/components/site/ui";
import StatusBadge from "@/components/StatusBadge";
import { DocButtons } from "@/components/admin/DocButtons";
import PayOnline from "@/components/PayOnline";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none";

export default function PortalReservation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [c, setC] = useState({ arrival_time: "", vehicles: "", emergency_contact: "", accepted_rules: false, accepted_pool_waiver: false, accepted_responsibility: false, accepted_damage: false, signed_name: "", signature: "", accessibility_needs: "", special_requests: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const load = () => api.get(`/portal/reservations/${id}`).then((res) => setR(res.data)).catch(() => setR(false));
  useEffect(() => { load(); }, [id]);

  if (r === null) return <Loading />;
  if (r === false) return <p className="text-muted-foreground">Reservation not found.</p>;

  const submitCheckin = async () => {
    if (!c.accepted_rules || !c.accepted_pool_waiver || !c.accepted_responsibility || !c.accepted_damage) { toast.error("Please accept all acknowledgments."); return; }
    if (!c.signature.trim() || !c.signed_name.trim()) { toast.error("Please provide your signature and name."); return; }
    setSaving(true);
    try {
      await api.post(`/portal/reservations/${id}/checkin`, { ...c, vehicles: c.vehicles.split(",").map((s) => s.trim()).filter(Boolean), arrival_time: c.arrival_time || r.arrival_time });
      toast.success("Digital check-in submitted.");
      setShowCheckin(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const cancel = async () => {
    try { await api.post(`/portal/reservations/${id}/cancel`); toast.success("Reservation cancelled."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const canCheckin = ["Approved", "Confirmed", "Deposit Requested", "Partially Paid", "Paid in Full"].includes(r.status);
  const canCancel = !["Cancelled", "Checked Out", "Archived", "Declined"].includes(r.status);

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/portal/reservations")} className="text-sm text-muted-foreground flex items-center gap-2 hover:text-gold"><ArrowLeft size={15} /> Back</button>
      <div className="glass rounded-lg p-6">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-label uppercase text-muted-foreground">{r.confirmation_number}</p>
            <h2 className="font-serif text-3xl mt-1">{r.experience_title}</h2>
            <p className="text-sm text-muted-foreground mt-2">{fmtDate(r.arrival_date)} → {fmtDate(r.departure_date)} · {r.adults} adults · {r.children} children</p>
          </div>
          <div className="text-right space-y-2">
            <StatusBadge status={r.status} testid="portal-res-status" />
            <p className="text-xs text-muted-foreground">Check-in: {r.checkin_status}</p>
          </div>
        </div>
      </div>

      {r.folio && (
        <div className="glass rounded-lg p-6">
          <h3 className="font-serif text-xl mb-4">Folio</h3>
          <div className="space-y-2 text-sm">
            {r.folio.items.map((it) => (
              <div key={it.id} className="flex justify-between"><span className={`text-muted-foreground ${it.kind === "discount" ? "text-pool" : ""}`}>{it.label}</span><span>{money(it.amount)}</span></div>
            ))}
            <div className="gold-line my-3" />
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{money(r.folio.total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{money(r.folio.paid)}</span></div>
            <div className="flex justify-between font-medium"><span>Balance due</span><span className="text-gold">{money(r.folio.balance)}</span></div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">Card details are never entered inside The D — payments are completed on a secure payment page.</p>
          <PayOnline rid={id} />
          <div className="mt-4 pt-4 border-t border-white/10">
            <DocButtons testidPrefix="portal-doc" docs={[
              { key: "confirmation", label: "Confirmation", path: `/portal/documents/reservation/${id}/confirmation`, filename: `${r.confirmation_number}-confirmation.pdf` },
              { key: "folio", label: "Folio Statement", path: `/portal/documents/reservation/${id}/folio`, filename: `${r.confirmation_number}-folio.pdf` },
              { key: "receipt", label: "Payment Receipt", path: `/portal/documents/reservation/${id}/receipt`, filename: `${r.confirmation_number}-receipt.pdf` },
              { key: "checkin-record", label: "Check-In Record", path: `/portal/documents/reservation/${id}/checkin-record`, filename: `${r.confirmation_number}-checkin.pdf` },
            ]} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {canCheckin && (
          <button onClick={() => setShowCheckin((s) => !s)} data-testid="portal-checkin-toggle" className="btn-gold px-6 py-3 text-sm flex items-center gap-2">
            <ClipboardCheck size={16} /> {r.checkin_status === "Submitted" ? "Update Check-In" : "Complete Digital Check-In"}
          </button>
        )}
        {canCancel && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="btn-ghost-gold px-6 py-3 text-sm flex items-center gap-2" data-testid="portal-cancel-btn"><Ban size={16} /> Cancel</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass border-gold/20">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Cancel this reservation?</AlertDialogTitle>
                <AlertDialogDescription>This will release your dates. Deposit refunds follow the cancellation policy.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={cancel} className="bg-red-600 hover:bg-red-700" data-testid="portal-cancel-confirm">Yes, cancel</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {showCheckin && (
        <div className="glass rounded-lg p-6 space-y-4" data-testid="checkin-form">
          <h3 className="font-serif text-2xl">Digital Check-In</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Confirm arrival time"><input type="time" className={inputCls} value={c.arrival_time} onChange={(e) => set("arrival_time", e.target.value)} /></Field>
            <Field label="Emergency contact"><input className={inputCls} value={c.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></Field>
          </div>
          <Field label="Vehicles & plates" hint="Separate with commas"><input className={inputCls} value={c.vehicles} onChange={(e) => set("vehicles", e.target.value)} /></Field>
          <Field label="Accessibility needs"><input className={inputCls} value={c.accessibility_needs} onChange={(e) => set("accessibility_needs", e.target.value)} /></Field>
          <Field label="Special requests"><textarea rows={2} className={inputCls} value={c.special_requests} onChange={(e) => set("special_requests", e.target.value)} /></Field>
          <div className="space-y-2 pt-2">
            {[["accepted_rules", "I accept the house rules"], ["accepted_pool_waiver", "I accept the pool safety waiver (no lifeguard on duty)"], ["accepted_responsibility", "I accept guest responsibility for my party"], ["accepted_damage", "I acknowledge the property damage policy"]].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={c[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 accent-gold" data-testid={`checkin-${k}`} /> {label}
              </label>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <Field label="Signed name" required><input className={inputCls} value={c.signed_name} onChange={(e) => set("signed_name", e.target.value)} data-testid="checkin-signed-name" /></Field>
            <Field label="Signature (type full name)" required><input className={inputCls} value={c.signature} onChange={(e) => set("signature", e.target.value)} data-testid="checkin-signature" /></Field>
          </div>
          <button onClick={submitCheckin} disabled={saving} className="btn-gold px-7 py-3 text-sm flex items-center gap-2" data-testid="checkin-submit">
            {saving ? <Loader2 className="animate-spin" size={16} /> : null} Submit Check-In
          </button>
        </div>
      )}
    </div>
  );
}
